import React, { useMemo } from 'react'
import { findDOMNode } from 'react-dom'
import { Route, useHistory, useRouteMatch } from 'react-router-dom'
import withLeftSidebar, { LeftSidebarProps } from 'components/Sidebar/withLeftSidebar'
import { FormattedMessage } from 'react-intl'
import {
  MenuItem,
  MenuItemAction,
} from 'components/Sidebar'
import LayoutSettings from '../LayoutSettings'
import KanBoIcon from 'components/KanBoIcon'
import LayoutGroup from './LayoutGroup'
import styled from 'styled-components'
import { sagaActions } from '../../sagas'
import {
  setIsSidebarVisible
} from 'store/redux/layout'
import {
  add as addLocalSetting,
  remove as removeLocalSetting
} from 'store/redux/localSettings'
import { useDispatch, useSelector } from 'react-redux'
import useMergedRef from '@react-hook/merged-ref'
import { createSelector } from 'reselect'
import makeLayoutPermissionSelector from '../../selectors/makeLayoutPermissionSelector'

const DragOverlay = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  z-index: 3;
`

type Props = LeftSidebarProps & {
  sortable: any
  id: number
}

const DeskSidebarItem = React.forwardRef<HTMLDivElement, Props>(({
  leftSidebar,
  sortable,
  id,
}, ref) => {
  const boardMatch = useRouteMatch<{ boardId: string }>(['/board/:boardId(\\d+)', '/board/:boardId(\\d+)/:viewId'])

  const mergedRef = useMergedRef(
    ref,
    ref => sortable.connectDragSource(findDOMNode(ref))
  )

  const history = useHistory()

  const dispatch = useDispatch()

  const unreadCount = useSelector<any, number>(useMemo(() => createSelector(
    [
      state => state['view-home'].layouts.byId[id],
      state => state['view-home'].layoutGroupsByKey,
      state => state['view-home'].unreadBoardActivitiesCount,
    ],
    (
      layout,
      layoutGroupsByKey,
      unreadBoardActivitiesCount,
    ) =>
      layout ? (
        layout.layoutGroups.reduce((count, layoutGroupId) => {
          layoutGroupsByKey[`${layout.id}-${layoutGroupId}`].elements.forEach(element => {
            if (element.type === 'board') {
              count += unreadBoardActivitiesCount[element.boardId] || 0
            }
          })

          return count
        }, 0)
       ) : 0
  ), [id]))
  
  const layout = useSelector<any, any>(state => state['view-home'].layouts.byId[id])
  const isCollapsed = useSelector<any, boolean>(state => state.kanbo.localSettings['collapseSidebarLayout-' + id])

  const showLayoutSettings = () => {
    leftSidebar.push(() => <LayoutSettings addToMyHomeLayout layoutId={id} />, { position: 'left' })
  }

  const handleClick = () => {
    dispatch(setIsSidebarVisible(false))

    history.push(`/home/${id}`)
  }

  const handleRemoveClick = () => {
    dispatch(sagaActions.requestRemoveMyLayout({ id }))
  }

  const handleCollapseClick = (event) => {
    event.stopPropagation()

    if (!isCollapsed) {
      dispatch(addLocalSetting({
        id: 'collapseSidebarLayout-' + id,
        value: true
      }))
    } else {
      dispatch(removeLocalSetting({
        id: 'collapseSidebarLayout-' + id
      }))
    }
  }

  const layoutPermissionSelector: any = useMemo(makeLayoutPermissionSelector, [])
  const layoutPermission = useSelector(state => layoutPermissionSelector(state, { layoutId: id }))

  const menu = useMemo(() => {
    const menu = [
      {
        icon: <KanBoIcon name='kanbo-16-info' />,
        title: <FormattedMessage id='DESK_SETTINGS' />,
        onClick: showLayoutSettings
      }
    ]

    const layoutGroupId = layout?.layoutGroups?.[0]
    if (layoutGroupId && boardMatch && layoutPermission.owner) {
      menu.push({
        icon: <KanBoIcon name='kanbo-16-plus' />,
        title: <FormattedMessage id='BOARD_ADD_CURRENT' />,
        onClick: () => {
          dispatch(sagaActions.requestAddBoardToLayoutGroup({
            layoutId: id,
            id: Number(boardMatch.params.boardId),
            layoutGroupId: layout?.layoutGroups?.[0],
            position: 0,
          }))
        }
      })
    }

    menu.push({
      icon: <KanBoIcon name='kanbo-16-remove' />,
      title: <FormattedMessage id='DESK_DETACH' />,
      onClick: handleRemoveClick
    })

    return menu
  }, [id, layout, boardMatch, layoutPermission])

  if (!layout) return null

  if (layout.isBusy) {
    return (
      <MenuItem
        icon={<KanBoIcon spin name='kanbo-16-loading' />}
        title={layout.name}
      />
    )
  } else if (layout.isLocked) {
    return (
      <div style={{ position: 'relative' }}>
        <MenuItem
          ref={mergedRef}
          icon={<KanBoIcon spin name='kanbo-16-loading' />}
          title={layout.name}
          menu={[{
            icon: <KanBoIcon name='kanbo-16-remove' />,
            title: <FormattedMessage id='DESK_DETACH' />,
            onClick: handleRemoveClick
          }]}
        />
        {sortable.isDragging && <DragOverlay />}
      </div>
    )
  } else {
    return (
      <div style={{ position: 'relative' }}>
        <Route
          exact
          path={`/home/${layout.id}`}
        >
          {({ match }) =>
            <MenuItem
              ref={mergedRef}
              icon={
                <KanBoIcon
                  name={
                    layout.isPublic
                      ? 'kanbo-24-desk-public-2'
                      : layout.isShared
                        ? 'kanbo-24-desk-shared'
                        : 'kanbo-24-desk'
                  }
                />
              }
              title={layout.name}
              selected={Boolean(match)}
              onClick={handleClick}
              badge={unreadCount > 999 ? 999 : unreadCount}
              menu={menu}
              actions={
                <MenuItemAction
                  icon={<KanBoIcon name={isCollapsed ? 'kanbo-16-chevron-left' : 'kanbo-16-chevron-down'} />}
                  onClick={handleCollapseClick}
                />
              }
            />
          }
        </Route>
        {!isCollapsed && layout.layoutGroups.map(id =>
          <LayoutGroup
            key={id}
            layoutId={layout.id}
            id={id}
          />
        )}
        {sortable.isDragging && <DragOverlay />}
      </div>
    )
  }
})

export default withLeftSidebar(DeskSidebarItem)
